import type { Icon } from "@phosphor-icons/react";
import {
  Archive,
  Books,
  Brain,
  ChatsCircle,
  Compass,
  FileText,
  Flask,
  Folders,
  Gear,
  Globe,
  House,
  MagnifyingGlass,
  PencilSimple,
  Pulse,
  Robot,
  Trash,
} from "@phosphor-icons/react";
import { ABILITY, type Abilities } from "~/api/types";
import { knowledgeLocation } from "~/features/analysis/knowledgeNavigationModel";
import { type FeatureNavItem, navSectionFor } from "~/registry";
import { SITE, SITE_CONFIG } from "~/site";

export interface NavItem {
  id: string;
  /** Natural-language translation key; rendered through `t()` at the call site. */
  title: string;
  icon: Icon;
  to: string;
  /** Gated on the *workspace's* server-supplied abilities. */
  visible?: (abilities: Abilities) => boolean;
  disabled?: boolean;
  /**
   * `false` keeps the destination in the launcher, search and pins but out of the sidebar's
   * link list — for an area the sidebar already shows as a browsable tree (Collections).
   */
  inSidebar?: false;
  /** Occasional destinations remain searchable/pinnable and live under More views. */
  secondary?: boolean;
  /**
   * Further path prefixes this destination owns — an area whose tabs live under more than one
   * path (Knowledge: `/knowledge…` and `/space…`) stays lit on every one of them.
   */
  alsoActiveOn?: string[];
}

export interface NavSection {
  id: string;
  title?: string;
  /** Starts folded until the viewer opens it — for long, occasional-use sections. */
  defaultCollapsed?: boolean;
  items: NavItem[];
}

// appcoil relabels three registry items ("Marketplace" was already the slate label; "My
// market" / "My CV" are appcoil-only). Keys are t() translation keys, resolved downstream by
// the render site — never translate them here. Catalog entries landed in s10.
const APPCOIL_NAV_LABELS: Record<string, string> = {
  market: "Marketplace",
  "market-mine": "My market",
  "market-profile": "My CV",
};

const withSiteLabel = (item: FeatureNavItem): FeatureNavItem =>
  SITE === "appcoil" ? { ...item, title: APPCOIL_NAV_LABELS[item.id] ?? item.title } : item;

/**
 * Data, not JSX — the sidebar renders it as links and the console bar's product picker
 * renders the same object as a menu. One list, two surfaces, no drift.
 */
const sectionById: Record<string, NavSection> = {
  // The everyday core — kept short on purpose (2026-10-11: the flat list had grown to 18).
  primary: {
    id: "primary",
    items: [
      { id: "home", title: "Home", icon: House, to: "/" },
      { id: "search", title: "Search", icon: MagnifyingGlass, to: "/search" },
      { id: "talk", title: "Talk", icon: ChatsCircle, to: "/talk" },
      // The workspace thinker (2026-10-23): the workspace's steward — one entry, daily, so it sits in the core.
      { id: "thinker", title: "Workspace thinker", icon: Robot, to: "/thinker" },
      // Registry-contributed (the subscribe platform lands here). A feature switched off in
      // sites.ts drops out of the sidebar with no edit to this file. Site argument stays
      // literal "slate" — registry identity-by-reference law (s03); labels are relabelled
      // afterward, per-render-site, not per-registry-mount.
      ...navSectionFor("slate", "primary").map(withSiteLabel),
    ],
  },
  work: {
    id: "work",
    title: "Work",
    items: [
      ...navSectionFor("slate", "work").map(withSiteLabel),
      { id: "activity", title: "Live activity", icon: Pulse, to: "/work/activity" },
    ],
  },
  workspace: {
    id: "workspace",
    title: "Workspace",
    items: [
      // The Collections tree below the links stands for this one in the sidebar.
      {
        id: "collections",
        title: "Collections",
        icon: Folders,
        to: "/collections",
        inSidebar: false,
      },
      { id: "documents", title: "Documents", icon: FileText, to: "/documents" },
      { id: "drafts", title: "Drafts", icon: PencilSimple, to: "/drafts" },
    ],
  },
  // Daily entry points, including the evolving story magazine. Every existing lens remains searchable and pinnable.
  knowledge: {
    id: "knowledge",
    title: "Knowledge",
    items: [
      ...navSectionFor("slate", "knowledge")
        .map(withSiteLabel)
        .map((item) => ({
          ...item,
          title: knowledgeLocation(item.to)?.item.label ?? item.title,
          secondary: !["briefing", "knowledge-visuals"].includes(item.id),
          ...(item.id === "knowledge-visuals" ? { alsoActiveOn: ["/knowledge/source"] } : {}),
        })),
      { id: "knowledge-explorer", title: "Knowledge explorer", icon: Brain, to: "/knowledge/explore", secondary: true },
      { id: "briefing-topics", title: "Connected subjects", icon: Brain, to: "/briefing/topics", secondary: true },
      {
        id: "knowledge-discoveries",
        title: "Research & watch",
        icon: Flask,
        to: "/knowledge/discoveries",
      },
      {
        id: "knowledge-triggers",
        title: "Triggers & questions",
        icon: Pulse,
        to: "/knowledge/triggers",
        secondary: true,
      },
      { id: "knowledge", title: "Knowledge health", icon: Books, to: "/knowledge", secondary: true },
      {
        id: "knowledge-browse",
        title: "Knowledge browser",
        icon: Books,
        to: "/knowledge/browse",
        secondary: true,
      },
      {
        id: "knowledge-tree",
        title: "Knowledge tree",
        icon: Books,
        to: "/knowledge/topdown",
        secondary: true,
      },
      {
        id: "knowledge-project",
        title: "Project map",
        icon: Folders,
        to: "/knowledge/project",
        secondary: true,
      },
      { id: "knowledge-space", title: "3D space", icon: Globe, to: "/space", secondary: true },
      {
        id: "knowledge-history",
        title: "Forecast history",
        icon: Books,
        to: "/knowledge/briefings",
        secondary: true,
      },
      {
        id: "discover",
        title: "Workspace reading",
        icon: Compass,
        to: "/discover",
        secondary: true,
      },
      { id: "world", title: "World", icon: Globe, to: "/world", secondary: true },
      { id: "reasoning", title: "Reasoning", icon: Brain, to: "/reasoning", secondary: true },
      {
        id: "research",
        title: "Research laboratory",
        icon: Flask,
        to: "/research",
        secondary: true,
      },
    ],
  },
  // Both sections are registry-contributed end to end: their items exist only while the
  // feature is enabled for slate, and `visibleNavSections` drops a section that empties out.
  marketplace: {
    id: "marketplace",
    title: "Marketplace",
    items: navSectionFor("slate", "marketplace").map(withSiteLabel),
  },
  people: {
    id: "people",
    title: "People",
    items: navSectionFor("slate", "people").map(withSiteLabel),
  },
  storage: {
    id: "storage",
    title: "Storage",
    items: [
      { id: "archive", title: "Archive", icon: Archive, to: "/archive" },
      { id: "trash", title: "Trash", icon: Trash, to: "/trash" },
    ],
  },
  admin: {
    id: "admin",
    title: "Admin",
    items: [
      {
        id: "settings",
        title: "Settings",
        icon: Gear,
        to: "/settings",
        visible: (abilities) => abilities[ABILITY.workspaceUpdate] === true,
      },
    ],
  },
};

// Order follows the active site's profile (`SITE_CONFIG.navSectionOrder`) — slate's order is
// the pre-existing fixed list by construction, appcoil promotes marketplace/people to the
// front. `navSectionOrder` is a plain `string[]` (siteConfigs.ts is import-free, so it cannot
// share NavSection's id union), so the lookup is filtered rather than asserted: an unknown id
// drops out instead of leaking `undefined` into the exported array.
export const navSections: NavSection[] = SITE_CONFIG.navSectionOrder
  .map((id) => sectionById[id])
  .filter((section): section is NavSection => section !== undefined);

export function visibleNavSections(abilities: Abilities): NavSection[] {
  return navSections
    .map((section) => ({
      ...section,
      items: section.items.filter((item) => (item.visible ? item.visible(abilities) : true)),
    }))
    .filter((section) => section.items.length > 0);
}

/** Exact for the root, prefix at a segment boundary otherwise — `/space` must not claim `/spaceship`. */
function matchesPath(to: string, pathname: string): boolean {
  if (to === "/") {
    return pathname === "/";
  }

  return pathname === to || pathname.startsWith(`${to}/`);
}

/** The paths a destination owns: its own, plus any `alsoActiveOn`. */
export function ownedPaths(item: { to: string; alsoActiveOn?: string[] }): string[] {
  return [item.to, ...(item.alsoActiveOn ?? [])];
}

/** The length of the most specific owned path that matches, or -1 when none does. */
export function matchStrength(
  item: { to: string; alsoActiveOn?: string[] },
  pathname: string,
): number {
  return ownedPaths(item).reduce(
    (best, path) => (matchesPath(path, pathname) && path.length > best ? path.length : best),
    -1,
  );
}

// A profile may leave Admin out of its menus without locking anyone out of their settings: the
// console bar's Profile and Workspace menus reach /settings on every site.
const ALWAYS_REACHABLE_SECTIONS = new Set(["admin"]);

/**
 * True when this path belongs to an area the site keeps behind the scenes: the most specific nav
 * item owning it sits in a section the profile's `navSectionOrder` leaves out (appcoil, 2026-10-24:
 * marketplace, people and work only). A path no nav item owns — a deep link inside a visible
 * area, sign-in, invites — is never hidden, and a path owned equally by a shown and a hidden item
 * stays shown. Slate's profile lists every section, so nothing is hidden there.
 */
export function isPathHiddenOnSite(
  pathname: string,
  order: readonly string[] = SITE_CONFIG.navSectionOrder,
): boolean {
  let best = -1;
  let hidden = false;
  for (const section of Object.values(sectionById)) {
    const shown = order.includes(section.id) || ALWAYS_REACHABLE_SECTIONS.has(section.id);
    for (const item of section.items) {
      const strength = matchStrength(item, pathname);
      if (strength > best) {
        best = strength;
        hidden = !shown;
      } else if (strength === best && strength >= 0 && shown) {
        hidden = false;
      }
    }
  }
  return hidden;
}

/**
 * Active-route matching, most-specific-wins: a nested item (`/knowledge/topdown` were it an item)
 * owns the highlight on its own route instead of lighting up its parent (`/knowledge`) alongside it.
 */
export function isNavItemActive(item: NavItem, pathname: string): boolean {
  const strength = matchStrength(item, pathname);
  if (strength < 0) {
    return false;
  }

  return !navSections.some((section) =>
    section.items.some((other) => other !== item && matchStrength(other, pathname) > strength),
  );
}
